// src/components/ProjectCard.js
import React, { useEffect, useState, useCallback, useMemo } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { cn } from "../lib/utils";
import { imgSet } from "../lib/img";

const ProjectCard = ({ cards = [] }) => {
  const [selectedIndex, setSelectedIndex] = useState(null);

  const isOpen = selectedIndex !== null;
  const selected = useMemo(
    () => (selectedIndex !== null ? cards[selectedIndex] : null),
    [selectedIndex, cards]
  );
  const selectedImg = useMemo(
    () => (selected ? imgSet(selected.thumbnail) : null),
    [selected]
  );

  const openAt = useCallback((idx) => setSelectedIndex(idx), []);
  const handleClose = useCallback(() => setSelectedIndex(null), []);

  const prev = useCallback(() => {
    if (cards.length === 0) return;
    setSelectedIndex((i) => (i === null ? i : (i - 1 + cards.length) % cards.length));
  }, [cards.length]);

  const next = useCallback(() => {
    if (cards.length === 0) return;
    setSelectedIndex((i) => (i === null ? i : (i + 1) % cards.length));
  }, [cards.length]);

  // Esc to close, ←/→ to navigate
  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e) => {
      if (e.key === "Escape") { e.preventDefault(); handleClose(); }
      else if (e.key === "ArrowRight") { e.preventDefault(); next(); }
      else if (e.key === "ArrowLeft") { e.preventDefault(); prev(); }
    };
    window.addEventListener("keydown", onKey, { passive: false });
    return () => window.removeEventListener("keydown", onKey);
  }, [isOpen, next, prev, handleClose]);

  // 🔥 Pre-decode adjacent images to slash INP on next/prev
  useEffect(() => {
    if (selectedIndex === null || cards.length < 2) return;
    const decodeAt = (idx) => {
      const src = cards[idx]?.thumbnail;
      if (!src) return;
      const img = new Image();
      img.src = imgSet(src).thumb;
      img.decode?.().catch(() => {});
    };
    const n = (selectedIndex + 1) % cards.length;
    const p = (selectedIndex - 1 + cards.length) % cards.length;
    // idle is best; fallback to timeout
    const id = window.requestIdleCallback
      ? requestIdleCallback(() => { decodeAt(n); decodeAt(p); }, { timeout: 800 })
      : setTimeout(() => { decodeAt(n); decodeAt(p); }, 150);
    return () => {
      if (window.cancelIdleCallback && typeof id === "number") cancelIdleCallback(id);
      else clearTimeout(id);
    };
  }, [selectedIndex, cards]);

  return (
    <>
      <div className="w-full h-full p-10 grid grid-cols-1 md:grid-cols-3 max-w-4xl mx-auto gap-4">
        {cards.map((card, idx) => {
          const v = imgSet(card.thumbnail);
          return (
          <button
            type="button"
            key={card.id ?? `card-${idx}`}
            onClick={() => openAt(idx)}
            className={cn(
              card.className,
              "relative overflow-hidden bg-white rounded-xl aspect-square mx-auto border-4 border-gray-100 w-full h-full focus:outline-none focus:ring-2 focus:ring-black/40"
            )}
            aria-label={card.alt ? `Open ${card.alt}` : "Open image"}
          >
            <img
              // Smallest variant as the fallback; browser upgrades via srcSet.
              src={v.thumb}
              fetchPriority={idx === 0 ? "high" : "auto"}
              // Thumbnails render at ~300px, so 400w usually wins; 800w covers retina.
              srcSet={v.srcSet}
              sizes="(min-width: 768px) 33vw, 100vw"
              width={1024} height={1024}
              decoding="async"
              loading={idx === 0 ? "eager" : "lazy"}
              className="object-cover absolute inset-0 w-full h-full bg-white"
              alt={card.alt ?? "thumbnail"}
            />
          </button>
          );
        })}
      </div>

      <AnimatePresence>
        {isOpen && selected && (
          <>
            {/* Backdrop */}
            <motion.button
              onClick={handleClose}
              className="fixed inset-0 bg-black/70 z-10"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              aria-label="Close overlay"
            />

            {/* Modal */}
            <motion.div
              className="fixed inset-0 z-20 flex items-center justify-center p-4 sm:p-6"
              role="dialog"
              aria-modal="true"
              initial={{ opacity: 0, scale: 0.98 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.98 }}
              transition={{ duration: 0.18, ease: "easeOut" }}
            >
              <div className="relative max-h-[90vh] max-w-[92vw] sm:max-w-[86vw] shadow-2xl rounded-2xl overflow-hidden transform-gpu">
                <img
                  // Full-size variant for the modal; browser still down-picks on small screens.
                  src={selectedImg?.full}
                  srcSet={selectedImg?.srcSet}
                  sizes="(min-width: 768px) 86vw, 92vw"
                  width={1600} height={1200}
                  decoding="async"
                  loading="eager"
                  className="object-contain max-w-[92vw] sm:max-w-[86vw] max-h-[90vh] select-none"
                  alt={selected.alt ?? "selected image"}
                  draggable={false}
                />

                {selected.content ? (
                  <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-black/0 p-3 sm:p-4 text-white text-sm">
                    {selected.content}
                  </div>
                ) : null}

                {/* Prev / Next */}
                <button
                  onClick={prev}
                  className="absolute left-2 top-1/2 -translate-y-1/2 bg-black/60 hover:bg-black/80 text-white text-2xl w-10 h-10 rounded-full flex items-center justify-center"
                  aria-label="Previous image"
                >
                  ‹
                </button>
                <button
                  onClick={next}
                  className="absolute right-2 top-1/2 -translate-y-1/2 bg-black/60 hover:bg-black/80 text-white text-2xl w-10 h-10 rounded-full flex items-center justify-center"
                  aria-label="Next image"
                >
                  ›
                </button>

                {/* Close */}
                <button
                  onClick={handleClose}
                  className="absolute top-5 right-3 z-30 bg-black/60 hover:bg-black/80 text-white text-2xl w-10 h-10 rounded-full flex items-center justify-center"
                  aria-label="Close image"
                >
                  ×
                </button>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </>
  );
};

export default React.memo(ProjectCard);
